import React from 'react'
import Video from 'components/Video'
import { ArrowDropDownCircle, InsertDriveFile } from '@mui/icons-material'
import { useDragAndDropTarget } from 'hooks/useDragAndDropTarget'
import { useFragment, useSubscription } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { StoredVideo_Video$key } from '__generated__/StoredVideo_Video.graphql'
import { useTheme } from '@mui/material/styles'
import { CircularProgress, Typography } from '@mui/material'
import { Nullable } from 'types'
import { usePolledBackgroundQuery } from 'hooks/usePolledBackgroundQuery'
import { StoredVideo_Subscription } from '__generated__/StoredVideo_Subscription.graphql'
import useStyles from './styles'

export interface IStoredVideoProps {
  editable: boolean
  videoFragmentRef: Nullable<StoredVideo_Video$key>
  isUploading: boolean
  uploadProgress: number
  onFileDrop: (file: File) => void
}

function StoredVideo(props: IStoredVideoProps) {
  const { classes } = useStyles()
  const theme = useTheme()
  const { editable, videoFragmentRef, isUploading, uploadProgress = 0, onFileDrop } = props
  const { dropMode, dropReady, targetProps } = useDragAndDropTarget({ onFileDrop })

  const video = useFragment(graphql`
    fragment StoredVideo_Video on Video {
      id
      optimizedUrl
      posterUrl
      status

      ...Video_Video
    }
  `, videoFragmentRef)

  // TODO: this finalizingUpload check should be removed
  // It's needed only because the Video component somehow messes up displaying the video when it's not finished processing
  const finalizingUpload = Boolean(video?.id && (!video?.optimizedUrl || !video?.posterUrl) && !['processing_failed', 'processing_rejected'].includes(video?.status))

  useSubscription<StoredVideo_Subscription>({
    subscription: graphql`
      subscription StoredVideo_Subscription($id: ID) {
        videoUpdated(videoId: $id) {
          video {
            ...StoredVideo_Video
          }
        }
      }
    `,
    variables: {
      id: video?.id,
    },
  })

  // Workaround for unreliable subscription
  usePolledBackgroundQuery(graphql`
    query StoredVideo_PolledBackgroundQuery($id: ID) {
      video: optionalNode(id: $id) {
        ... on Video {
          ...StoredVideo_Video
        }
      }
    }
  `, {
    id: video?.id,
  }, {
    pollCondition: finalizingUpload,
  })

  return (
    <div className={classes.container} {...targetProps}>
      {(isUploading || finalizingUpload) && (
        <div className={classes.placeholderWrapper} data-cy="video-uploading-message">
          <div className={classes.placeholder}>
            <div style={{ marginBottom: theme.spacing(2) }}>
              <CircularProgress variant="indeterminate" />
            </div>

            {isUploading ? `Uploading video, ${Math.round(uploadProgress * 100)}%...` : 'Finalizing upload...'}
          </div>
        </div>
      )}

      {video && !isUploading && !finalizingUpload && !dropMode && (
        <div className={classes.videoWrapper} style={editable ? undefined : { marginBottom: 0 }}>
          <Video
            fullWidth
            videoFragmentRef={video}
            className={classes.video}
            rounded={false}
            pictureProps={{ width: undefined }}
            iconSize="large"
          />
        </div>
      )}

      {(!video || dropMode) && !isUploading && (
        <div className={classes.placeholderWrapper}>
          <div className={classes.placeholder}>
            <div className={classes.placeholderCircle} style={dropMode ? { transform: 'scale(7, 6)', opacity: 0 } : undefined} />
            {!dropMode && (<InsertDriveFile />)}
            {dropMode && (<ArrowDropDownCircle className={classes.arrow} />)}
          </div>

          <div
            className={classes.placeholderArea}
            style={{
              opacity: dropMode ? 1 : 0,
              backgroundColor: dropReady ? 'rgba(0, 0, 0, 0.067)' : undefined,
            }}
          />

          <div className={classes.placeholderDropText}>
            <Typography variant="body1">
              {video && 'Drop new video here'}
              {!video && 'Drop video here'}
            </Typography>
          </div>
        </div>
      )}
    </div>
  )
}

export default React.memo(StoredVideo)
